import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Package, Tag, Calendar, User, Loader2, BarChart3, QrCode } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const LabelReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('design-wise');
  
  // Design-wise report state
  const [designSearch, setDesignSearch] = useState('');
  const [designs, setDesigns] = useState([]);
  const [designsLoading, setDesignsLoading] = useState(false);
  const [designsTotal, setDesignsTotal] = useState(0);
  const [designsPage, setDesignsPage] = useState(0);
  
  // USN lookup state
  const [usnSearch, setUsnSearch] = useState('');
  const [usnResult, setUsnResult] = useState(null);
  const [usnLoading, setUsnLoading] = useState(false);
  const [usnSearched, setUsnSearched] = useState(false);

  // Fetch design-wise data
  const fetchDesignWiseData = useCallback(async (search = '', skip = 0) => {
    setDesignsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('skip', skip.toString());
      params.append('limit', '50');
      
      const response = await apiClient.get(`/main-labels/reports/design-wise-count?${params}`);
      setDesigns(response.data.designs || []);
      setDesignsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching design-wise data:', error);
      toast.error('Failed to fetch design-wise report');
    } finally {
      setDesignsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (activeTab === 'design-wise') {
      fetchDesignWiseData(designSearch, designsPage * 50);
    }
  }, [activeTab, designsPage, fetchDesignWiseData]);

  // Debounced search for designs
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'design-wise') {
        setDesignsPage(0);
        fetchDesignWiseData(designSearch, 0);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [designSearch, activeTab, fetchDesignWiseData]);

  // USN lookup handler
  const handleUsnLookup = async () => {
    if (!usnSearch.trim()) {
      toast.error('Please enter a USN code');
      return;
    }
    
    setUsnLoading(true);
    setUsnSearched(true);
    try {
      const response = await apiClient.get(`/main-labels/reports/usn-lookup?usn=${encodeURIComponent(usnSearch.trim())}`);
      setUsnResult(response.data);
    } catch (error) {
      console.error('Error looking up USN:', error);
      toast.error('Failed to lookup USN');
      setUsnResult(null);
    } finally {
      setUsnLoading(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#F5F0E8]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/sections/goods-transfer')}
                className="text-white hover:bg-white/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold font-display">Label Reports</h1>
                <p className="text-sm text-gray-400">USN assignments and design-wise analysis</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-[#2D5F4F]" />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border border-[#E5DFD0]">
            <TabsTrigger 
              value="design-wise" 
              className="data-[state=active]:bg-[#2D5F4F] data-[state=active]:text-white"
              data-testid="design-wise-tab"
            >
              <Package className="w-4 h-4 mr-2" />
              Design-wise Count
            </TabsTrigger>
            <TabsTrigger 
              value="usn-lookup" 
              className="data-[state=active]:bg-[#2D5F4F] data-[state=active]:text-white"
              data-testid="usn-lookup-tab"
            >
              <QrCode className="w-4 h-4 mr-2" />
              USN Lookup
            </TabsTrigger>
          </TabsList>

          {/* Design-wise Count Tab */}
          <TabsContent value="design-wise">
            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1A1F2E]">
                    Design-wise USN Count
                  </CardTitle>
                  <span className="text-sm text-gray-500">
                    Total Designs: {designsTotal}
                  </span>
                </div>
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by design name, barcode, or category..."
                    value={designSearch}
                    onChange={(e) => setDesignSearch(e.target.value)}
                    className="pl-10 border-[#E5DFD0]"
                    data-testid="design-search-input"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {designsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
                  </div>
                ) : designs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No design data found</p>
                    {designSearch && <p className="text-sm mt-1">Try a different search term</p>}
                  </div>
                ) : (
                  <>
                    <div className="rounded-lg border border-[#E5DFD0] overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-semibold text-[#1A1F2E]">#</TableHead>
                            <TableHead className="font-semibold text-[#1A1F2E]">Design Name</TableHead>
                            <TableHead className="font-semibold text-[#1A1F2E]">Category</TableHead>
                            <TableHead className="font-semibold text-[#1A1F2E] text-center">USN Count</TableHead>
                            <TableHead className="font-semibold text-[#1A1F2E] text-center">Barcodes</TableHead>
                            <TableHead className="font-semibold text-[#1A1F2E]">Latest Print</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {designs.map((design, index) => (
                            <TableRow key={index} className="hover:bg-gray-50">
                              <TableCell className="text-gray-500">{designsPage * 50 + index + 1}</TableCell>
                              <TableCell className="font-medium text-[#1A1F2E]">
                                {design.design_name}
                              </TableCell>
                              <TableCell>
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                  {design.category}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="px-3 py-1 bg-[#2D5F4F] text-white rounded-full text-sm font-semibold">
                                  {design.usn_count}
                                </span>
                              </TableCell>
                              <TableCell className="text-center text-gray-600">
                                {design.barcode_count}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {formatDate(design.latest_print)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Pagination */}
                    {designsTotal > 50 && (
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-sm text-gray-500">
                          Showing {designsPage * 50 + 1} - {Math.min((designsPage + 1) * 50, designsTotal)} of {designsTotal}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDesignsPage(p => Math.max(0, p - 1))}
                            disabled={designsPage === 0}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDesignsPage(p => p + 1)}
                            disabled={(designsPage + 1) * 50 >= designsTotal}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* USN Lookup Tab */}
          <TabsContent value="usn-lookup">
            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-[#1A1F2E]">
                  USN Lookup
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Search for a USN to view its assigned item details
                </p>
                <div className="flex gap-2 mt-4">
                  <div className="relative flex-1">
                    <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Enter USN code (e.g., J31PWPGDG)"
                      value={usnSearch}
                      onChange={(e) => setUsnSearch(e.target.value.toUpperCase())}
                      onKeyPress={(e) => e.key === 'Enter' && handleUsnLookup()}
                      className="pl-10 border-[#E5DFD0] font-mono"
                      data-testid="usn-search-input"
                    />
                  </div>
                  <Button
                    onClick={handleUsnLookup}
                    disabled={usnLoading || !usnSearch.trim()}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                    data-testid="usn-search-btn"
                  >
                    {usnLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    <span className="ml-2">Search</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {usnLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
                  </div>
                ) : !usnSearched ? (
                  <div className="text-center py-12 text-gray-500">
                    <QrCode className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Enter a USN code and click Search</p>
                  </div>
                ) : !usnResult?.found ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                      <Search className="w-8 h-8 text-red-500" />
                    </div>
                    <p className="text-lg font-medium text-gray-700">USN Not Found</p>
                    <p className="text-sm text-gray-500 mt-1">{usnResult?.message || 'No assignment found for this USN'}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* USN Info Header */}
                    <div className="flex items-center gap-4 p-4 bg-[#2D5F4F]/5 rounded-lg">
                      <div className="w-12 h-12 rounded-lg bg-[#2D5F4F] flex items-center justify-center">
                        <Tag className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold font-mono text-[#1A1F2E]">{usnResult.usn_code}</p>
                        <p className="text-sm text-gray-500">USN Code</p>
                      </div>
                      {usnResult.assignment?.pair_number && (
                        <div className="ml-auto px-4 py-2 bg-purple-100 text-purple-800 rounded-lg">
                          <span className="text-lg font-bold">#{usnResult.assignment.pair_number}</span>
                          <p className="text-xs">Pair Number</p>
                        </div>
                      )}
                    </div>

                    {/* Item Details */}
                    {usnResult.item && (
                      <div className="p-4 border border-[#E5DFD0] rounded-lg">
                        <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          Item Details
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Design Name</p>
                            <p className="font-medium text-[#1A1F2E]">{usnResult.item.design_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Category</p>
                            <p className="font-medium text-[#1A1F2E]">{usnResult.item.category || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Shade</p>
                            <p className="font-medium text-[#1A1F2E]">{usnResult.item.shade || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Size</p>
                            <p className="font-medium text-[#1A1F2E]">{usnResult.item.size || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">MRP</p>
                            <p className="font-medium text-[#1A1F2E]">₹{usnResult.item.mrp || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Description</p>
                            <p className="font-medium text-[#1A1F2E]">{usnResult.item.description || '-'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Assignment Details */}
                    <div className="p-4 border border-[#E5DFD0] rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Assignment Details
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Item Barcode</p>
                          <p className="font-mono font-medium text-[#1A1F2E]">{usnResult.assignment?.item_barcode || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Optional Barcode</p>
                          <p className="font-mono font-medium text-[#1A1F2E]">{usnResult.assignment?.optional_barcode || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Pricing Type</p>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                            {usnResult.assignment?.pricing_type || '-'}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Template</p>
                          <p className="font-medium text-[#1A1F2E]">{usnResult.assignment?.template_type || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Assigned At</p>
                          <p className="font-medium text-[#1A1F2E]">{formatDate(usnResult.assignment?.assigned_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Assigned By</p>
                          <p className="font-medium text-[#1A1F2E] flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {usnResult.assignment?.assigned_by || '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default LabelReports;
